import { useState, useRef } from 'react';
import { updateProfile } from 'firebase/auth';
import { auth, db } from '../firebase';
import { compressImage } from '../utils/firestoreService';
import { doc, setDoc } from 'firebase/firestore';

const F = "'Inter',-apple-system,sans-serif";
const T = { orange:'var(--orange)', ink:'var(--ink)', surface:'var(--surface)', border:'var(--border)', muted:'var(--muted)', g1:'var(--surface2)', red:'#DC2626' };

export default function ProfileModal({ user, onClose, onSaved }) {
  const [name, setName]     = useState(user?.displayName || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg]       = useState('');
  const fileRef = useRef();

  const saveProfile = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await updateProfile(auth.currentUser, { displayName: name.trim() });
      setMsg('Profil mis à jour ✓');
      onSaved?.();
      setTimeout(onClose, 1200);
    } catch(e) { setMsg('Erreur : ' + e.message); }
    setSaving(false);
  };

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      // Avatar stocké en Firestore base64 (pas besoin de Firebase Storage)
      const { dataUrl } = await compressImage(file, 200, 0.85);
      // Sauvegarder dans Firestore
      await setDoc(doc(db, 'users', user.uid, 'profile', 'avatar'), {
        dataUrl, updatedAt: new Date().toISOString(),
      });
      // Mettre à jour le profil Auth avec la dataUrl comme photoURL
      await updateProfile(auth.currentUser, { photoURL: dataUrl });
      setMsg('Avatar mis à jour ✓');
      onSaved?.();
    } catch(e) { setMsg('Erreur : ' + e.message); }
    setUploading(false);
    fileRef.current.value = '';
  };

  const initials = name ? name.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2) : '?';

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(28,22,18,.6)',backdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2000,padding:'1rem'}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:T.surface,borderRadius:'20px',padding:'2rem',maxWidth:'380px',width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,.2)',fontFamily:F,animation:'popIn .3s ease'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1.5rem'}}>
          <div style={{fontSize:'17px',fontWeight:700,color:T.ink}}>Mon profil</div>
          <button onClick={onClose} style={{width:'30px',height:'30px',borderRadius:'50%',border:`1px solid ${T.border}`,background:T.g1,cursor:'pointer',fontSize:'16px',color:T.muted,display:'flex',alignItems:'center',justifyContent:'center',padding:0}}>×</button>
        </div>

        {/* Avatar */}
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'10px',marginBottom:'1.5rem'}}>
          <div style={{position:'relative'}}>
            <div style={{width:'80px',height:'80px',borderRadius:'50%',background:`linear-gradient(135deg,#F04E1A,#F5C842)`,display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden',border:`3px solid ${T.border}`,cursor:'pointer'}}
              onClick={()=>fileRef.current?.click()}>
              {user?.photoURL
                ? <img src={user.photoURL} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                : <span style={{fontFamily:"'Bangers',cursive",fontSize:'1.6rem',color:'#fff',letterSpacing:'.05em'}}>{initials}</span>}
            </div>
            <button onClick={()=>fileRef.current?.click()} disabled={uploading}
              style={{position:'absolute',bottom:0,right:0,width:'28px',height:'28px',borderRadius:'50%',background:T.orange,border:`2px solid ${T.surface}`,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',fontSize:'13px'}}>
              {uploading?'…':'✏️'}
            </button>
          </div>
          <div style={{fontSize:'12px',color:T.muted}}>Clique sur l'avatar pour changer la photo</div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatar} style={{display:'none'}}/>
        </div>

        {/* Nom */}
        <div style={{marginBottom:'1rem'}}>
          <label style={{display:'block',fontSize:'12px',fontWeight:600,color:T.muted,marginBottom:'5px',textTransform:'uppercase',letterSpacing:'.05em'}}>Nom affiché</label>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="Ton prénom ou pseudo"
            style={{width:'100%',padding:'11px 13px',border:`1.5px solid ${T.border}`,borderRadius:'9px',fontFamily:F,fontSize:'15px',color:T.ink,background:T.surface,outline:'none'}}
            onFocus={e=>{e.target.style.borderColor=T.orange;e.target.style.boxShadow='0 0 0 3px rgba(240,78,26,.10)';}}
            onBlur={e=>{e.target.style.borderColor=T.border;e.target.style.boxShadow='none';}}/>
        </div>

        <div style={{fontSize:'12px',color:T.muted,marginBottom:'1rem'}}>Email : {user?.email}</div>

        {msg && <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:'8px',padding:'9px 12px',fontSize:'13px',fontWeight:600,color:'#15803D',marginBottom:'12px'}}>{msg}</div>}

        <div style={{display:'flex',gap:'10px'}}>
          <button onClick={onClose} style={{flex:1,padding:'11px',border:`1.5px solid ${T.border}`,borderRadius:'10px',background:T.surface,fontFamily:F,fontSize:'14px',fontWeight:600,color:T.muted,cursor:'pointer'}}>Annuler</button>
          <button onClick={saveProfile} disabled={saving||!name.trim()}
            style={{flex:2,padding:'11px',border:'none',borderRadius:'10px',background:T.orange,color:'#fff',fontFamily:F,fontSize:'14px',fontWeight:700,cursor:'pointer',boxShadow:'0 3px 10px rgba(240,78,26,.28)',opacity:saving?0.7:1}}>
            {saving?'Sauvegarde…':'Enregistrer'}
          </button>
        </div>
      </div>
      <style>{`@keyframes popIn{0%{opacity:0;transform:scale(.9)}70%{transform:scale(1.02)}100%{opacity:1;transform:scale(1)}}`}</style>
    </div>
  );
}
